// src/components/admin/RecordModal.js
import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';

const RecordModal = ({
  isOpen,
  onClose,
  onSave,
  record = null, // null for add, object for edit
  fields = [],
  title,
  loading = false
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = record !== null;

  // Initialize form data when modal opens or record changes
  useEffect(() => {
    if (isOpen) {
      if (isEditMode) {
        // Edit mode - populate with existing record
        setFormData({ ...record });
      } else {
        // Add mode - initialize with default values
        const initialData = {};
        fields.forEach(field => {
          initialData[field.key] = field.defaultValue || '';
        });
        setFormData(initialData);
      }
      setErrors({});
    }
  }, [isOpen, record, fields, isEditMode]);

  const handleInputChange = (fieldKey, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldKey]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[fieldKey]) {
      setErrors(prev => ({
        ...prev,
        [fieldKey]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    fields.forEach(field => {
      const value = formData[field.key];

      // Required field validation
      if (field.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
        newErrors[field.key] = `${field.label} is required`;
        return;
      }

      // Skip other validations if field is empty and not required
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        return;
      }

      // Type-specific validation
      if (field.type === 'number') {
        const numValue = Number(value);
        if (isNaN(numValue)) {
          newErrors[field.key] = `${field.label} must be a valid number`;
        } else if (field.min !== undefined && numValue < field.min) {
          newErrors[field.key] = `${field.label} must be at least ${field.min}`;
        } else if (field.max !== undefined && numValue > field.max) {
          newErrors[field.key] = `${field.label} must be no more than ${field.max}`;
        }
      }

      if (field.type === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          newErrors[field.key] = `${field.label} must be a valid email address`;
        }
      }

      // Custom validation function
      if (field.validate) {
        const customError = field.validate(value, formData);
        if (customError) {
          newErrors[field.key] = customError;
        }
      }
    });

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(formData, isEditMode);
      onClose();
    } catch (error) {
      // Handle server-side errors
      if (error.fieldErrors) {
        setErrors(error.fieldErrors);
      } else {
        setErrors({ general: error.message || 'An error occurred while saving' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field) => {
    const value = formData[field.key] || '';
    const error = errors[field.key];
    const fieldId = `field-${field.key}`;

    const baseInputClasses = `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
      error ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
    }`;

    switch (field.type) {
      case 'text':
      case 'email':
        return (
          <input
            id={fieldId}
            type={field.type}
            value={value}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            className={baseInputClasses}
            disabled={field.disabled}
          />
        );

      case 'number':
        return (
          <input
            id={fieldId}
            type="number"
            value={value}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            min={field.min}
            max={field.max}
            step={field.step || 'any'}
            className={baseInputClasses}
            disabled={field.disabled}
          />
        );

      case 'textarea':
        return (
          <textarea
            id={fieldId}
            value={value}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            rows={field.rows || 3}
            className={baseInputClasses}
            disabled={field.disabled}
          />
        );

      case 'select':
        return (
          <select
            id={fieldId}
            value={value}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            className={baseInputClasses}
            disabled={field.disabled}
          >
            <option value="">Select {field.label}...</option>
            {field.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'checkbox':
        return (
          <input
            id={fieldId}
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => handleInputChange(field.key, e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            disabled={field.disabled}
          />
        );

      case 'conditional':
        // Use custom render function if provided
        if (field.renderField && typeof field.renderField === 'function') {
          return field.renderField(field, value, formData, handleInputChange);
        }
        // Fallback to text input
        return (
          <input
            id={fieldId}
            type="text"
            value={value}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            className={baseInputClasses}
            disabled={field.disabled}
          />
        );

      default:
        return (
          <input
            id={fieldId}
            type="text"
            value={value}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            className={baseInputClasses}
            disabled={field.disabled}
          />
        );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title || (isEditMode ? 'Edit Record' : 'Add New Record')}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="p-6">
        {/* General error message */}
        {errors.general && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{errors.general}</p>
          </div>
        )}

        {/* Form fields */}
        <div className="space-y-4">
          {fields
            .filter(field => {
              // Check if field should be shown based on showWhen condition
              if (field.showWhen && typeof field.showWhen === 'function') {
                return field.showWhen(formData);
              }
              return true; // Show field by default if no showWhen condition
            })
            .map((field) => (
            <div key={field.key}>
              <label 
                htmlFor={`field-${field.key}`}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              
              {renderField(field)}
              
              {errors[field.key] && (
                <p className="mt-1 text-sm text-red-600">{errors[field.key]}</p>
              )}
              
              {field.help && (
                <p className="mt-1 text-sm text-gray-500">{field.help}</p>
              )}
            </div>
          ))}
        </div>

        {/* Form actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            disabled={isSubmitting || loading}
          >
            {isSubmitting && (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {isEditMode ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default RecordModal;